import React, { useState } from "react";
import CardFrame from "./CardFrame";
import { useHistory } from "react-router-dom";
import Calendar from "./Calendar";
import styled from "styled-components";
import { getPosts } from "api";
import { useQuery } from "@tanstack/react-query";
import { connect } from "react-redux";
import moment from "moment";
import noPost from "assets/images/noposts.svg";
import { socialIcons } from "constants/post";
import Button from "components/global/Button";

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;

  .postContainer {
    width: 100%;
    height: 100%;
    max-height: 750px;
    overflow-y: auto;
  }

  .postCard {
    padding: 12px;
    border-radius: 8px;
    background: #f8f8f8;
    width: 100%;
    display: flex;
    gap: 12px;
    align-items: center;

    img {
      border-radius: 8px;
    }

    .postContent {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      font-style: normal;
      line-height: 20px;
      font-size: 14px;
      color: #2e2e3a;
    }

    .horizontalRule {
      width: 1px;
      height: 16px;
      background: #e2e2ea;
    }
  }
`;

export const PostContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const SkeletonCard = styled.div`
  padding: 12px;
  border-radius: 8px;
  background: #f8f8f8;
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;

  .skeletonImage {
    width: 53px;
    height: 50px;
    background: #e2e2ea;
    border-radius: 8px;
    animation: skeleton-loading 1.5s infinite ease-in-out;
  }

  .skeletonText {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    .line {
      height: 14px;
      background: #e2e2ea;
      border-radius: 4px;
      animation: skeleton-loading 1.5s infinite ease-in-out;
    }

    .line.short {
      width: 50%;
    }

    .line.full {
      width: 100%;
    }

    .skeletonMeta {
      display: flex;
      align-items: center;
      gap: 8px;

      .circle {
        width: 16px;
        height: 16px;
        background: #e2e2ea;
        border-radius: 50%;
        animation: skeleton-loading 1.5s infinite ease-in-out;
      }

      .rule {
        width: 1px;
        height: 16px;
        background: #e2e2ea;
      }
    }
  }

  @keyframes skeleton-loading {
    0% {
      background-color: #e0e0e0;
    }
    50% {
      background-color: #f0f0f0;
    }
    100% {
      background-color: #e0e0e0;
    }
  }

  @media (max-width: 768px) {
    .skeletonImage {
      width: 40px;
      height: 40px;
    }

    .line {
      height: 12px;
    }
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  position: absolute;
  left: 96.066px;
  top: 296px;

  @media screen and (max-width: 768px) {
    position: unset;
    img {
      display: none;
    }
  }
`;

const PostSchedule = ({ brand, active_account }) => {
  const history = useHistory();
  const [activeDate, setActiveDate] = useState(moment().format("YYYY-MM-DD"));

  const { data: scheduleData, isLoading: scheduleLoading } = useQuery({
    queryKey: ["schedule", activeDate],
    queryFn: () =>
      getPosts(active_account?.id, brand?.id, "scheduled", 1, {
        scheduled_date: activeDate,
      }),
    select: (data) => data?.data?.data?.posts,
    enabled: !!activeDate,
  });

  return (
    <Wrapper>
      <CardFrame
        headerText="Posting schedule for this week"
        actionBtn="See more"
        onClick={() => history.push("/dashboard/calendar")}
      >
        <div className="fullWidth flexRow justifyCenter">
          <Calendar
            actions={[moment().format("YYYY-MM-DD")]}
            activeDate={activeDate}
            setActiveDate={setActiveDate}
          />
        </div>
        <div className="fullWidth">
          {scheduleLoading ? (
            <PostContainer>
              {Array(4)
                .fill(0)
                ?.map((_, index) => (
                  <SkeletonCard key={index}>
                    <div className="skeletonImage"></div>
                    <div className="skeletonText">
                      <div className="line full"></div>
                      <div className="skeletonMeta">
                        <div className="circle"></div>
                        <div className="rule"></div>
                        <div className="line short"></div>
                      </div>
                    </div>
                  </SkeletonCard>
                ))}
            </PostContainer>
          ) : scheduleData?.length === 0 ? (
            <EmptyState>
              <img src={noPost} alt="No posts" width="51px" height="54px" />
              <div className="textCenter colorGrey6 l3">
                You currently do not have any <br /> post scheduled for today
              </div>
              <Button
                text="Create post"
                height="36px"
                onClick={() => history.push("/dashboard/posts/calendar-view?post_action=create_post")}
              />
            </EmptyState>
          ) : (
            <div className="postContainer flexColumn gap12">
              {scheduleData?.map((post) => (
                <div key={post.id} className="postCard flexRow gap12">
                  {post?.media_thumbnail[0] && (
                    <img
                      src={post?.media_thumbnail[0]}
                      alt=""
                      width="53px"
                      height="50"
                    />
                  )}
                  <div className="flexColumn gap8">
                    <p className="postContent">{post?.content}</p>
                    <div className="flexRow gap8 l3 alignCenter">
                      <img src={socialIcons.circle[post?.platform]} alt="" />
                      <div className="horizontalRule"></div>
                      <p>{moment(post?.schedule_date).format("HH:mm ")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardFrame>
    </Wrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    active_account: state.active_account,
    brand: state.brand,
  };
};

export default connect(mapStateToProps)(PostSchedule);
